'use client';

import { ownedImageProps } from '@lib/asset-renditions';
import type { ReactNode } from 'react';
import { ImageProps } from '@types';
import Tag, { FeatureTags } from '@dvd/components/Tag';
import { css, styled } from 'styled-components';
import WistiaLogo from '@dvd/components/WistiaLogo';
import { disclosureStyles } from '@dvd/styles/disclosure';
import { Icon } from '@wistia/ui';
import * as Styled from '@lib/styles';
import Image from '@dvd/components/asset-image';

/**
 * The parts of a data accordion a page author writes.
 *
 * Split out from the props a composing module supplies, so a list of entries
 * can share those without repeating them per entry.
 */
export type DataAccordionContent = {
  /**
   * Title for the source (internal) integration
   */
  sourceTitle: string;

  /**
   * Title for the target (external) integration
   */
  targetTitle: string;

  /**
   * Optional title to summarize the purpose of mapped data fields
   */
  headline?: string;

  /**
   * Description that gives an overview of the purpose and insight gleaned from mapped data fields
   * - Use a simple string (e.g. "A sentence of words.") for straight copy.
   * - Use HTML to apply simple formatting (e.g. A sentence with an <strong>important</strong> word.)
   */
  description: ReactNode;

  /**
   * A row that displays the titles of the source and integration fields.
   */
  rows: { sourceFieldName: string; integrationFieldName: string }[];

  /**
   * Colored feature tags that show what features are available for the integrated service/platform.
   *
   * Examples:
   * - webinars, video, channels, custom
   */
  tags: FeatureTags[];

  /**
   * Whether the accordion should be open on initial render
   */
  defaultOpen?: boolean;
};

export type DataAccordionProps = DataAccordionContent & {
  /**
   * Logomark image of the target (external) integration, displayed alongside
   * the target title.
   */
  targetImage: ImageProps;
};

const logoSizeStyle = css`
  max-width: 1rem;
  max-height: 1rem;

  ${({ theme }) => theme.mq.minWidth.medium} {
    max-width: 1.25rem;
    max-height: 1.25rem;
  }
`;

/* Reusable blocks to style individual fields in the data accordion.*/

const fieldBorderStyle = css`
  border-top: ${({ theme }) => `1px solid ${theme.colors.blue100}`};
`;

const fieldGapStyle = css`
  gap: ${({ theme }) => theme.spacing[0.5]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    gap: ${({ theme }) => theme.spacing[0.75]};
  }
`;

const fieldPaddingStyle = css`
  padding: ${({ theme }) => theme.spacing[0.75]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[1.5]}`};
  }
`;

const rowHoverStyle = css`
  &:hover {
    background-color: ${({ theme }) => theme.colors.blue100};
  }
`;

const DataAccordionContainer = styled.li`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radius.sm};
  box-shadow: ${({ theme }) => theme.elevation.elevation2()};
  list-style-type: none;
  padding: ${({ theme }) => `${theme.spacing[0.5]} 0 ${theme.spacing[0.5]}  0`};

  h4 {
    margin: 0;
  }
`;

const DataAccordionDetails = styled.details`
  ${disclosureStyles}

  .collapseIcon {
    display: none;
  }

  &[open] {
    .expandIcon {
      display: none;
    }

    .collapseIcon {
      display: initial;
    }
  }
`;

const DataAccordionSummary = styled.summary`
  ${rowHoverStyle}
`;

/*
  The heading keeps the accordion in the page's semantic outline, and sits
  inside the summary so it names the disclosure trigger.
*/
const SummaryHeading = styled.h4`
  display: flex;
`;

/* Headings only allow phrasing content as children, so we use spans instead of divs to group and individually style the data accordion headers for mapped data.*/

const HeaderContainer = styled.span`
  ${fieldPaddingStyle}
  flex: 0 0 50%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  abbr.dataMappingArrow {
    color: ${({ theme }) => theme.colors.blue800};
    font-family: var(--font-inter);
    font-size: 1.5rem;
    font-weight: ${({ theme }) => theme.typography.fontWeight.body};
    text-decoration: none;
  }

  .detailsIcon {
    color: ${({ theme }) => theme.colors.blue500};
  }
`;

const HeaderTitleWrapper = styled.span`
  flex: 0 1 75%;
  align-self: start;
  text-align: left;

  span {
    display: block;
  }

  svg,
  img {
    margin-block-end: ${({ theme }) => theme.spacing[0.5]};
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    align-self: center;

    svg,
    img {
      margin-block-end: 0;
    }

    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[0.75]};
  }
`;

const HeaderWistiaLogo = styled(WistiaLogo)`
  ${logoSizeStyle}
`;

const HeaderTargetLogo = styled(Image)`
  ${logoSizeStyle}
  border-radius: ${({ theme }) => theme.radius.std};
  height: auto;
  width: 100%;
`;

const PanelSummary = styled.div`
  ${fieldBorderStyle}
  ${fieldPaddingStyle}
  ${rowHoverStyle}
`;
const PanelSummaryHeadline = styled(Styled.BodySm)`
  font-weight: ${({ theme }) => theme.typography.fontWeight.superTitle};
  margin-block-end: ${({ theme }) => theme.spacing[0.25]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    margin-block-end: ${({ theme }) => theme.spacing[0.5]};
  }
`;
const PanelSummaryDescription = styled(Styled.BodySm)`
  margin: 0;

  &.longDescription {
    /* Remove bottom margin from last element when HTML is used for description. */
    :last-child {
      margin-block-end: 0;
    }
  }
`;

const PanelMappingTable = styled.dl`
  margin: 0;

  dt,
  dd {
    ${fieldPaddingStyle}

    flex: 0 0 50%;
    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};

    margin: 0;

    ${({ theme }) => theme.mq.minWidth.large} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
    }
  }
`;
const PanelMappingTableRow = styled.div`
  ${rowHoverStyle}
  ${fieldBorderStyle}

  display: flex;
`;

const TagContainer = styled.div`
  ${fieldBorderStyle}
  ${fieldGapStyle}
  ${fieldPaddingStyle}
  ${rowHoverStyle}

  background-color: transparent;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;
const TagHeader = styled(Styled.BodyXs)`
  color: ${({ theme }) => theme.colors.grey400};
  font-weight: ${({ theme }) => theme.typography.fontWeight.superTitle};

  margin: 0;
  text-transform: uppercase;
`;

const TagList = styled.ul`
  ${fieldGapStyle}

  list-style-type: none;
  margin: 0;
  padding: 0;

  display: flex;
`;
const TagListItem = styled.li`
  margin: 0;
  padding: 0;
`;

/**
 * Shared data accordion for integrations.
 * Used to display integration connections and show how individual data fields are mapped between Wistia (source) and the integrated platform/service (target).
 */
const DataAccordion = ({
  sourceTitle,
  targetTitle,
  headline,
  description,
  rows,
  targetImage,
  tags,
  defaultOpen = false,
}: DataAccordionProps) => {
  const descriptionIsString = typeof description === 'string';

  return (
    <DataAccordionContainer>
      <DataAccordionDetails open={defaultOpen}>
        <DataAccordionSummary>
          <SummaryHeading>
            <HeaderContainer>
              <HeaderTitleWrapper>
                <HeaderWistiaLogo
                  aria-hidden={true}
                  iconOnly={true}
                  isOptical={true}
                />
                <span>{sourceTitle} </span>
              </HeaderTitleWrapper>
              <abbr className="dataMappingArrow" title="data is mapped to">
                →
              </abbr>
            </HeaderContainer>
            <HeaderContainer>
              <HeaderTitleWrapper>
                <HeaderTargetLogo
                  aria-hidden={true}
                  sizes="20px"
                  src={targetImage.src}
                  {...ownedImageProps(targetImage.src)}
                  width={targetImage.width}
                  height={targetImage.height}
                  alt={targetImage.alt}
                />
                <span>{targetTitle}</span>
              </HeaderTitleWrapper>
              <Icon
                type="caret-up"
                size="lg"
                className="detailsIcon expandIcon"
              />
              <Icon
                type="caret-down"
                size="lg"
                className="detailsIcon collapseIcon"
              />
            </HeaderContainer>
          </SummaryHeading>
        </DataAccordionSummary>

        <PanelSummary>
          {headline ? (
            <PanelSummaryHeadline>{headline}</PanelSummaryHeadline>
          ) : null}
          {descriptionIsString ? (
            <PanelSummaryDescription as="p">
              {description}
            </PanelSummaryDescription>
          ) : (
            <PanelSummaryDescription as="div" className="longDescription">
              {description}
            </PanelSummaryDescription>
          )}
        </PanelSummary>

        <PanelMappingTable>
          {rows.map(({ sourceFieldName, integrationFieldName }, index) => {
            return (
              <PanelMappingTableRow key={index}>
                <dt>{sourceFieldName}</dt>
                <dd>{integrationFieldName}</dd>
              </PanelMappingTableRow>
            );
          })}
        </PanelMappingTable>
      </DataAccordionDetails>

      <TagContainer>
        <TagHeader>Available for</TagHeader>
        <TagList>
          {tags.map((title, index) => (
            <TagListItem key={index}>
              <Tag tag={title} />
            </TagListItem>
          ))}
        </TagList>
      </TagContainer>
    </DataAccordionContainer>
  );
};

export default DataAccordion;
